import { i18nMixin } from '@/modules/neodash/mixins';
import { Client } from '@/store/modules/authentication/interfaces';
import { SaleType } from '@/modules/business-plan/modules/models/constants/saleType';
import {
  columnGoalSetting,
  labelColumnGoalSetting,
  numberColumnGoalSetting,
  percentColumnGoalSetting,
  radioColumnGoalSetting,
  inputManualColumnGoalSetting,
  IEconomicGroupGoalsDetails,
  hideGoalSettingCell,
  inputTotalManualColumnGoalSetting,
} from '@/modules/business-plan/modules/approval/pages/volvo-goal-settings/types';
import { goalTypeEnum } from '@/modules/business-plan/modules/models/enums/goalTypeEnum';

const translate = i18nMixin.methods.i18n;

export class GoalSettingColumns {
  public readonly translate = translate;
  public readonly labelIndustry: string = translate('Common.Industry');
  public readonly labelGroup: string = translate('Common.Group');
  public readonly labelDealer: string = translate('Common.Dealer');
  public readonly labelShare: string = translate('Common.Share');
  public readonly labelUnmetDemand: string = translate('Common.UnmetDemand');
  public readonly labelManualInput: string = translate('Common.GoalInput');
  public readonly labelFinalTarget: string = translate('Common.FinalTarget');

  public readonly currentYear: number;
  public readonly nextYear: number;
  public readonly lastYear: number;

  constructor(yearReference: number, public saleType: SaleType, private client: Client) {
    this.lastYear = yearReference - 2;
    this.currentYear = yearReference - 1;
    this.nextYear = yearReference;
  }

  public get saleTypeInitials(): string {
    return translate(this.saleType.initials);
  }

  public get familiesToHide(): number[] {
    return Array.from([97]);
  }

  public get familiesOnlyView(): number[] {
    return Array.from([96, 104]);
  }

  public getInputShareTotalProp: (obj: IEconomicGroupGoalsDetails) => number =
    obj => obj.industry.nextYear;

  public get iterator(): columnGoalSetting[] {
    const { client, saleTypeInitials, lastYear, currentYear } = this;
    const { labelIndustry, labelGroup, labelShare } = this;
    const nameClient = client.name;

    const join = (lst: string[]) => lst.join(' ');
    const pare = (label: number | string): string => `(${label})`;
    const principalLabel = year => join([nameClient, saleTypeInitials, pare(year)]);

    const getMonthValue = value => value / 12;
    const getMonthValue2021 = value => value / 12;
    const getMonthValueCustom = (value, month) => value / month;
    const getMonthValueByFamily = (value, idFamily) => {
      const families = [];
      if (families.some(x => x === idFamily)) {
        return value / 10;
      }

      return getMonthValue(value);
    }

    const getShareGroupTotalValue = (industry, customIndustry) => !customIndustry ? industry : customIndustry;

    const isInputAvailable = !this.saleType.isAllSalesTypes;
    const dontHide: hideGoalSettingCell = obj => false;
    const hideOnNotCalc: hideGoalSettingCell = obj => !obj.isCalcGoal || this.familiesToHide.includes(obj.id) || this.familiesOnlyView.includes(obj.id);
    const hideShareFamily: hideGoalSettingCell = obj => this.familiesToHide.includes(obj.id);

    const product: labelColumnGoalSetting = {
      title: 'Família',
      typeElement: 'label',
      getterLabel: obj => obj.name.toUpperCase(),
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const groupLastYear: numberColumnGoalSetting = {
      title: principalLabel(lastYear),
      distinctValues: false,
      distinctByFamilies: [],
      typeElement: 'number',
      getterValue: obj => obj.sale.lastYear,
      getterValuePerMonth: obj => getMonthValue(obj.sale.lastYear),
      getterNameSegment: obj => obj.nameSegment,
      hide: dontHide,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const groupThisYear: numberColumnGoalSetting = {
      title: principalLabel(currentYear),
      distinctValues: false,
      distinctByFamilies: [],
      typeElement: 'number',
      getterValue: obj => obj.sale.actualYear,
      getterValuePerMonth: obj => getMonthValueCustom(obj.sale.actualYear, obj.familyRuleValue),
      getterNameSegment: obj => obj.nameSegment,
      saleType: saleTypeInitials,
      showInfo: true,
      hide: dontHide,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const industryLastYear: numberColumnGoalSetting = {
      title: join([labelIndustry, saleTypeInitials, labelGroup, pare(lastYear)]),
      distinctValues: true,
      distinctByFamilies: [98, 102],
      typeElement: 'number',
      getterValue: obj => obj.industry.lastYear,
      getterValuePerMonth: obj => getMonthValue(obj.industry.lastYear),
      getterNameSegment: obj => obj.nameSegment,
      hide: dontHide,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const industryThisYear: numberColumnGoalSetting = {
      title: join([labelIndustry, saleTypeInitials, labelGroup, pare(currentYear)]),
      distinctValues: true,
      distinctByFamilies: [98, 102],
      typeElement: 'number',
      getterValue: obj => obj.industry.actualYear,
      getterValuePerMonth: obj => getMonthValueByFamily(obj.industry.actualYear, obj.idFamily),
      getterNameSegment: obj => obj.nameSegment,
      showInfo: true,
      hide: dontHide,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const shareGroupOverIndustryLastYear: percentColumnGoalSetting = {
      title: join([labelShare, labelGroup, pare(lastYear)]),
      typeElement: 'percent',
      getterTotal: obj => obj.industry.lastYear,
      getterIndustryTotal: obj => obj.industry.lastYear,
      getterOver: obj => obj.sale.lastYear,
      withSubtract: false,
      hide: hideShareFamily,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const shareGroupOverIndustryActualYear: percentColumnGoalSetting = {
      title: join([labelShare, labelGroup, pare(currentYear)]),
      typeElement: 'percent',
      distinctByFamilies: [98, 102],
      getterTotal: obj => getShareGroupTotalValue(obj.industry.actualYear, obj.customIndustry ? obj.customIndustry.actualYear : null),
      getterIndustryTotal: obj => obj.industry.actualYear,
      getterOver: obj => obj.sale.actualYear,
      getterMonthOverValue: obj => getMonthValueByFamily(obj.sale.actualYear, obj.idFamily),
      getterMonthIndustryTotal: obj => {
        const value = getMonthValueByFamily(obj.industry.actualYear, obj.idFamily);
        return value;
      },
      withSubtract: false,
      saleType: saleTypeInitials,
      showInfo: true,
      hide: hideShareFamily,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const businessPlan: radioColumnGoalSetting = {
      title: 'Meta (B.P)',
      typeElement: 'number',
      distinctValues: false,
      distinctByFamilies: [98, 102],
      isInput: true,
      isRadio: isInputAvailable,
      type: goalTypeEnum.BusinessPlan,
      getterValue: obj => obj.businessPlan,
      getterValuePerMonth: obj => getMonthValue2021(obj.businessPlan),
      getterNameSegment: obj => obj.nameSegment,
      getterOver: x => x.businessPlan,
      hide: hideOnNotCalc,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const manual: inputTotalManualColumnGoalSetting = {
      title: this.labelManualInput,
      typeElement: 'number',
      distinctValues: false,
      distinctByFamilies: [98, 102],
      getterValue: obj => obj.goalValueTotal,
      getterValuePerMonth: obj => getMonthValue2021(obj.goalValue),
      getterNameSegment: obj => obj.nameSegment,
      getterOver: x => {
        return x.goalValue;
      },
      isManualInput: true,
      isInput: true,
      type: goalTypeEnum.Manual,
      hide: hideOnNotCalc,
      isInputAvailable: isInputAvailable,
      calculateProportional: (value: number, goals: IEconomicGroupGoalsDetails[]) => {
        const total = goals.reduce((acc, curr) => acc + curr.businessPlan, 0);
        goals = goals.map(x => {
          x.goalValue = total > 0 ? value * (x.businessPlan / total) : 0;
          x.inputValue = x.goalValue;
          x.goalValueTotal = value;
          x.goalTypeSelected = goalTypeEnum.Manual;
          return x;
        });
      },
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const finalTarget: numberColumnGoalSetting = {
      title: `${this.labelFinalTarget} (2025)`,
      typeElement: 'number',
      distinctValues: false,
      distinctByFamilies: [],
      getterValue: obj => {
        return obj.goalValue;
      },
      getterValuePerMonth: obj => getMonthValue2021(obj.goalValue),
      getterNameSegment: obj => obj.nameSegment,
      hide: dontHide,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const industryNextYear: numberColumnGoalSetting = {
      title: join([labelIndustry, labelGroup, '(2025)']),
      typeElement: 'number',
      distinctValues: true,
      distinctByFamilies: [98, 102],
      getterValue: obj => obj.industry.nextYear,
      getterValuePerMonth: obj => getMonthValue2021(obj.industry.nextYear),
      getterNameSegment: obj => obj.nameSegment,
      hide: dontHide,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const growUpIndustry: percentColumnGoalSetting = {
      title: join(['Cresc. ', labelIndustry, saleTypeInitials, labelGroup, '(25vs24)']),
      typeElement: 'percent',
      distinctByFamilies: [98, 102],
      getterTotal: x => x.industry.nextYear,
      getterIndustryTotal: obj => obj.industry.nextYear,
      getterOver: x => x.industry.actualYearEstimated,
      getterMonthOverValue: obj => getMonthValueByFamily(obj.industry.actualYearEstimated, obj.idFamily),
      getterMonthIndustryTotal: obj => {
        const value = getMonthValue(obj.industry.nextYear);
        return value;
      },
      saleType: saleTypeInitials,
      withSubtract: true,
      hide: dontHide,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    const shareGoalNextYear: percentColumnGoalSetting = {
      title: join([labelShare, labelGroup, '(2025)']),
      typeElement: 'percent',
      distinctByFamilies: [98, 102],
      getterTotal: x => x.industry.nextYear,
      getterIndustryTotal: obj => obj.industry.nextYear,
      getterOver: x => {
        return x.goalValue;
      },
      withSubtract: false,
      hide: hideShareFamily,
      onlyView: (familyId) => this.familiesOnlyView.includes(familyId)
    };

    return [
      product,
      industryLastYear,
      industryThisYear,
      groupLastYear,
      groupThisYear,
      shareGroupOverIndustryLastYear,
      shareGroupOverIndustryActualYear,
      businessPlan,
      manual,
      finalTarget,
      industryNextYear,
      growUpIndustry,
      shareGoalNextYear
    ];
  }
}
